import { useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useConnectionStore } from '@/stores/connectionStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useSessionHistoryStore } from '@/stores/sessionHistoryStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { validateTemplate, type TemplateDefinition } from '@/types/template';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import {
  resolveTemplateConnections,
  patchWorkflowConnections,
  computeTemplateMockedNodes,
  type TemplateConnectionResolutionResult,
  type ConnectionResolution,
} from '@/utils/templates';
import { getFetchedTemplateExtras } from '@/services/templateService';
import { getImportedTemplateExtras } from '@/stores/importedTemplateStore';
import { findCompatibleConnectionsByApiId } from '@/services/connectionMatchingService';
import { telemetryService } from '@/services/telemetryService';
import { useManagedModels } from '@/hooks/useManagedModels';
import { extractConnectionMapping } from '@/services/connectionService';
import { walkAllActions } from '@/utils/workflow';
import { isCodingAgentHarnessType } from '@/types/workflowDefinition';

// =============================================================================
// WORKFLOW-NAME PLACEHOLDER REWRITING
// =============================================================================

/**
 * Replace `#workflowname#` placeholders in a template with the actual workflow
 * name. Upstream Azure/LogicAppsTemplates use this placeholder in connection
 * reference names and parameter names to namespace them per workflow. The
 * replacement is done via JSON serialization so placeholders inside expression
 * strings and object keys are both handled.
 *
 * When `workflowName` is empty (e.g. the identity hasn't been loaded yet),
 * strip the leading `_` along with the placeholder so we end up with a clean
 * name (`Method`, `sql`) rather than a dangling-underscore (`Method_`, `sql_`).
 *
 * Returns a shallow-cloned template with the rewritten fields. If no
 * placeholders are found the original template is returned as-is.
 */
export function rewriteWorkflowNamePlaceholders(
  template: TemplateDefinition,
  workflowName: string,
): TemplateDefinition {
  const placeholder = '#workflowname#';
  const json = JSON.stringify(template);
  if (!json.includes(placeholder)) return template;

  const rewritten = workflowName
    ? json.split(placeholder).join(workflowName)
    : json.split(`_${placeholder}`).join('').split(placeholder).join('');
  return JSON.parse(rewritten) as TemplateDefinition;
}

// =============================================================================
// WORKFLOW-PARAMETER STRIPPING
// =============================================================================

/**
 * Remove the workflow-parameters concept from a template before it is applied.
 *
 * The portal no longer supports template/workflow parameters, so any
 * `parameters('…')` reference baked into a template's action inputs is blanked
 * and the parameter declarations are dropped. Blanking is done on the
 * serialized JSON (same technique as the `#workflowname#` rewrite) so it
 * catches references nested anywhere in the definition.
 *
 * Each interpolation segment that contains a `parameters(` call collapses to
 * an empty string, leaving the surrounding literal text intact:
 *   `"${parameters('Method_Foo')}"`                            → `""`
 *   `"/v2/datasets/${encodeURIComponent(parameters('A'))},${encodeURIComponent(parameters('B'))}/query/sql"`
 *                                                              → `"/v2/datasets/,/query/sql"`
 *
 * Required fields left empty by this will surface the normal UI validation
 * error — that is the intended behaviour.
 */
export function stripWorkflowParameters(template: TemplateDefinition): TemplateDefinition {
  const json = JSON.stringify(template);
  const blanked = json
    // `${ … parameters(…) … }` interpolation segments
    .replace(/\$\{[^{}]*parameters\([^{}]*\}/g, '')
    // `@{ … parameters(…) … }` interpolation segments
    .replace(/@\{[^{}]*parameters\([^{}]*\}/g, '')
    // bare `@parameters('…')` values
    .replace(/@parameters\('[^']*'\)/g, '');

  // The blanking regexes are heuristic and can, on pathological inputs
  // (escaped quotes in a name, deeply nested `parameters(...)` calls), blank
  // across a JSON delimiter and produce invalid JSON. Rather than letting a
  // hard `SyntaxError` abort the whole template-apply in the user's face, fall
  // back to the original (unblanked) template — references stay intact and any
  // required-but-empty fields surface through normal UI validation instead.
  type WithParams = TemplateDefinition & {
    workflowDefinition?: { definition?: { parameters?: unknown } };
    parameters?: unknown;
  };
  let result: WithParams;
  try {
    result = JSON.parse(blanked) as WithParams;
  } catch (err) {
    console.warn('stripWorkflowParameters: blanked template was not valid JSON, applying unmodified', err);
    result = JSON.parse(json) as WithParams;
  }

  // Drop the parameter declarations entirely.
  if (result.workflowDefinition?.definition && 'parameters' in result.workflowDefinition.definition) {
    delete result.workflowDefinition.definition.parameters;
  }
  if ('parameters' in result) {
    delete result.parameters;
  }

  return result as TemplateDefinition;
}

function preferManagedTemplateAgents(
  template: TemplateDefinition,
  defaultModelId: string | undefined,
): TemplateDefinition {
  if (!defaultModelId ||
    !template.connectionRequirements.some((requirement) => requirement.connectorType === 'agent')) {
    return template;
  }

  const workflowDefinition = structuredClone(template.workflowDefinition);
  const convertedReferences = new Set<string>();
  walkAllActions(workflowDefinition.definition.actions, (_names, action) => {
    if (action.type !== 'Agent') return;
    const inputs = action.inputs as Record<string, unknown> | undefined;
    const parameters = inputs?.parameters as Record<string, unknown> | undefined;
    if (!inputs || parameters?.agentModelType !== 'AzureOpenAI') return;
    const modelConfigurations = inputs?.modelConfigurations as Record<string, unknown> | undefined;
    const model1 = modelConfigurations?.model1 as Record<string, unknown> | undefined;
    const referenceName = model1?.referenceName;
    if (typeof referenceName !== 'string' || Object.keys(modelConfigurations ?? {}).length !== 1 ||
      !template.connectionRequirements.some((req) => req.connectorType === 'agent' && req.referenceKey === referenceName)) {
      return;
    }

    const settings = parameters.agentModelSettings as Record<string, unknown> | undefined;
    const harness = settings?.agentHarness as Record<string, unknown> | undefined;
    const topLevelSettings = inputs.agentModelSettings as Record<string, unknown> | undefined;
    const topLevelHarness = topLevelSettings?.agentHarness as Record<string, unknown> | undefined;
    if (isCodingAgentHarnessType(harness?.type) ||
      isCodingAgentHarnessType(topLevelHarness?.type)) return;
    const {
      deploymentModelProperties: _byomModel,
      agentHistoryReductionSettings: _byomHistoryReduction,
      agentChatCompletionSettings,
      ...managedSettings
    } = settings ?? {};
    const completionSettings = agentChatCompletionSettings as Record<string, unknown> | undefined;
    const {
      temperature: _byomTemperature,
      topP: _byomTopP,
      maxTokens: _byomMaxTokens,
      frequencyPenalty: _byomFrequencyPenalty,
      presencePenalty: _byomPresencePenalty,
      builtinTools: _foundryTools,
      metadata: _v1Metadata,
      ...remainingCompletionSettings
    } = completionSettings ?? {};
    inputs.parameters = {
      ...parameters,
      agentModelType: 'MicrosoftManaged',
      deploymentId: defaultModelId,
      ...(settings ? {
        agentModelSettings: {
          ...managedSettings,
          ...(completionSettings ? { agentChatCompletionSettings: remainingCompletionSettings } : {}),
        },
      } : {}),
    };
    delete inputs.modelConfigurations;
    convertedReferences.add(referenceName);
  });
  if (convertedReferences.size === 0) return template;

  const remainingReferences = new Set(Object.values(extractConnectionMapping(workflowDefinition)));
  walkAllActions(workflowDefinition.definition.actions, (_names, action) => {
    const inputs = action.inputs as Record<string, unknown> | undefined;
    const modelConfigurations = inputs?.modelConfigurations as Record<string, unknown> | undefined;
    for (const configuration of Object.values(modelConfigurations ?? {})) {
      if (!configuration || typeof configuration !== 'object') continue;
      const referenceName = (configuration as Record<string, unknown>).referenceName;
      if (typeof referenceName === 'string') remainingReferences.add(referenceName);
    }
  });
  const retiredReferences = new Set(
    [...convertedReferences].filter((key) => !remainingReferences.has(key)),
  );
  const connectionsConfiguration = template.connectionsConfiguration
    ? structuredClone(template.connectionsConfiguration)
    : undefined;
  for (const key of retiredReferences) {
    if (connectionsConfiguration?.agentConnections) {
      delete connectionsConfiguration.agentConnections[key];
    }
  }

  return {
    ...template,
    workflowDefinition,
    connectionRequirements: template.connectionRequirements.filter(
      (requirement) => !requirement.referenceKey || !retiredReferences.has(requirement.referenceKey),
    ),
    ...(connectionsConfiguration ? { connectionsConfiguration } : {}),
  };
}

// =============================================================================
// CONNECTIONS CONFIGURATION MERGING
// =============================================================================

/**
 * Merge two ConnectionsConfiguration objects. The template's connections are
 * added to the existing connections, with template values taking precedence
 * for any duplicate keys (though duplicates should be rare).
 *
 * connections.json is shared across all workflows in the app, so we must
 * preserve existing connections when applying a template — otherwise we'd
 * break other workflows that depend on them.
 */
function mergeConnectionsConfiguration(
  existing: ConnectionsConfiguration | null | undefined,
  template: ConnectionsConfiguration | null | undefined,
): ConnectionsConfiguration | null {
  // If neither exists, return null
  if (!existing && !template) return null;

  // If only one exists, return it
  if (!existing) return template ?? null;
  if (!template) return existing;

  // Merge all connection types
  return {
    // Deep merge each connection category
    managedApiConnections: {
      ...(existing.managedApiConnections ?? {}),
      ...(template.managedApiConnections ?? {}),
    },
    serviceProviderConnections: {
      ...(existing.serviceProviderConnections ?? {}),
      ...(template.serviceProviderConnections ?? {}),
    },
    agentConnections: {
      ...(existing.agentConnections ?? {}),
      ...(template.agentConnections ?? {}),
    },
    agentMcpConnections: {
      ...(existing.agentMcpConnections ?? {}),
      ...(template.agentMcpConnections ?? {}),
    },
  };
}

/**
 * Build placeholder connection configuration entries for unresolved connections.
 * These entries allow the workflow to load with proper API IDs so the designer
 * can show connector icons and prompt the user to configure the connections.
 *
 * For 'shared' connections (managed APIs): creates managedApiConnections entries
 * For 'inapp' connections (service providers): creates serviceProviderConnections entries
 * For 'agent' connections (model providers): creates agentConnections entries
 *
 * Writing the placeholder is what makes the canvas badge read "Connection
 * configuration incomplete" (yellow warning) instead of "Connection reference
 * not found" (red error): without an entry under the referenceKey, the
 * useNodeConnectionStatus lookup misses and falls into the not-found branch.
 */
function buildPlaceholderConnectionsConfig(
  unresolvedConnections: ConnectionResolution[],
): ConnectionsConfiguration | null {
  if (unresolvedConnections.length === 0) return null;

  const config: ConnectionsConfiguration = {};

  for (const resolution of unresolvedConnections) {
    const { requirement, connectorName } = resolution;
    // Use the referenceKey from the requirement if available, otherwise generate one
    const referenceKey = requirement.referenceKey ?? `${connectorName}_placeholder`;

    if (requirement.connectorType === 'shared') {
      // Managed API connection placeholder
      if (!config.managedApiConnections) {
        config.managedApiConnections = {};
      }
      config.managedApiConnections[referenceKey] = {
        api: { id: requirement.apiId },
        connection: { id: '' }, // Empty - needs configuration
        connectionRuntimeUrl: '',
        authentication: { type: 'ManagedServiceIdentity' },
      };
    } else if (requirement.connectorType === 'inapp') {
      // Service provider connection placeholder
      if (!config.serviceProviderConnections) {
        config.serviceProviderConnections = {};
      }
      config.serviceProviderConnections[referenceKey] = {
        serviceProvider: { id: requirement.apiId },
        displayName: `${connectorName} (needs configuration)`,
        // Required.Always on the runtime's ServiceProviderConnection model —
        // without it the whole connections(-draft).json fails to parse and
        // every draft run in the app 400s. An empty dict still reads as
        // not-configured portal-side (hasMeaningfulConfigValue).
        parameterValues: {},
      };
    } else if (requirement.connectorType === 'agent') {
      // Agent connection placeholder (Azure OpenAI, OpenAI, etc.)
      // modelProvider.id is what connectionService maps back into `api.id`,
      // which the agent branch of isConnectionReferenceComplete then matches.
      // NOTE: this shape is store-only scaffolding — the runtime cannot parse
      // it (AgentConnection requires type/endpoint/authentication), so
      // exportToConnectionsConfig drops it when serializing connections files.
      if (!config.agentConnections) {
        config.agentConnections = {};
      }
      config.agentConnections[referenceKey] = {
        modelProvider: { id: requirement.apiId },
        displayName: `${connectorName} (needs configuration)`,
      };
    }
  }

  return Object.keys(config).length > 0 ? config : null;
}

// =============================================================================
// PUBLIC TYPES
// =============================================================================

export interface TemplateApplyOptions {
  /** If true, skip the undo toast notification */
  silent?: boolean;
  /** User selections for connection requirements that had multiple matches.
   *  Key = connectorType, value = chosen referenceKey */
  connectionSelections?: Map<string, string>;
}

export interface TemplateApplyResult {
  success: boolean;
  /** Connection resolution details */
  connectionResult?: TemplateConnectionResolutionResult;
  /** Error message if validation or precondition check failed */
  error?: string;
}

// =============================================================================
// CONNECTION MAP BUILDER
// =============================================================================

/**
 * Build the `templateReferenceKey → resolvedReferenceKey` map that
 * `patchWorkflowConnections` consumes.
 *
 * For each resolution category:
 *  - `autoMatched`     → the single match is used as-is.
 *  - `needsSelection`  → prefer the caller's selection (keyed by
 *                        `connectorName`); otherwise auto-pick the first
 *                        compatible connection. Without this fallback the
 *                        workflow would land with its template-declared
 *                        `referenceKey` pointing at an entry that has no
 *                        record in connections.json — the action shows
 *                        "Connection reference not found" rather than the
 *                        intended "needs configuration".
 *  - `unresolved`      → intentionally not added: the template's referenceKey
 *                        is preserved and `buildPlaceholderConnectionsConfig`
 *                        writes a matching placeholder entry.
 *
 * Pure: no React, no store access.
 */
export function buildConnectionMap(
  connectionResult: TemplateConnectionResolutionResult,
  connectionSelections?: Map<string, string>,
): Map<string, string> {
  const connectionMap = new Map<string, string>();

  for (const resolution of connectionResult.autoMatched) {
    if (resolution.matchedReferenceKey) {
      const mapKey = resolution.requirement.referenceKey ?? resolution.connectorName;
      connectionMap.set(mapKey, resolution.matchedReferenceKey);
    }
  }

  for (const resolution of connectionResult.needsSelection) {
    const selectedKey =
      connectionSelections?.get(resolution.connectorName) ??
      resolution.compatibleConnections?.[0]?.referenceKey;
    if (selectedKey) {
      const mapKey = resolution.requirement.referenceKey ?? resolution.connectorName;
      connectionMap.set(mapKey, selectedKey);
    }
  }

  return connectionMap;
}

/**
 * Seed the next `mockedNodes` snapshot for an applied template.
 *
 * Thin wiring around the pure `computeTemplateMockedNodes`: looks up the
 * template's mock extras by template id (imported/session templates first,
 * then the fetched-template cache) and feeds them, the current workflow, and
 * the existing mocks into the pure compute. The caller writes the result back
 * with `workflowStore.setMockedNodes`, so this helper is unit-testable with
 * only the extras lookups mocked.
 *
 * Returns an empty seed when the template has no id or no extras. In every
 * case `computeTemplateMockedNodes` also drops mocks for nodes no longer in
 * the workflow and returns `{}` when no workflow is loaded, so existing user
 * edits survive only for nodes that still exist.
 */
export function buildTemplateMockSeed(
  templateId: string | undefined,
  workflow: Parameters<typeof computeTemplateMockedNodes>[0],
  existingMocks: Parameters<typeof computeTemplateMockedNodes>[1],
): ReturnType<typeof computeTemplateMockedNodes> {
  // Imported (session) templates shadow fetched ones with the same id,
  // matching the precedence useTemplates applies to the gallery list.
  const extras = templateId
    ? getImportedTemplateExtras(templateId) ?? getFetchedTemplateExtras(templateId)
    : undefined;
  return computeTemplateMockedNodes(
    workflow,
    existingMocks,
    extras?.mocks ?? {},
    extras?.triggerSampleData,
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useTemplateApply(enableManagedModels = true): {
  /** Apply a template definition to the current canvas */
  applyTemplate: (template: TemplateDefinition, options?: TemplateApplyOptions) => TemplateApplyResult;
  /** Check connection compatibility without applying */
  previewConnections: (template: TemplateDefinition) => TemplateConnectionResolutionResult;
  isManagedModelLoading: boolean;
} {
  // URL-level workflow name as a backstop for the store identity slot, which
  // depends on MSAL `account` and can land *after* a template is applied
  // (see WorkflowDesignerPage's currentWorkflowIdentity effect). The URL
  // segment is always present on the designer route, so reading it directly
  // means the `#workflowname#` rewrite has a real name to substitute even when
  // the identity store hasn't caught up yet.
  const { workflowName: urlWorkflowName, projectName } = useParams<{
    workflowName?: string;
    projectName?: string;
  }>();
  const { defaultModelId, isLoading: isManagedModelLoading } =
    useManagedModels(projectName, enableManagedModels);

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /** Snapshot current workflow/connections/parameters/notes into session history. */
  function captureCurrentSnapshot(description: string): void {
    const { history, captureSnapshot } = useSessionHistoryStore.getState();
    // Only capture if the session has been initialised (has at least one entry)
    if (history.length === 0) return;

    const workflow = useWorkflowStore.getState().workflow;
    const connections = useWorkflowStore.getState().connections;
    const parameters = useDraftModeStore.getState().draftParameters;
    const notes = useDraftModeStore.getState().draftNotes;

    if (!workflow) return;

    captureSnapshot({ workflow, connections, parameters, notes }, description);
  }

  /** Track whether the undo for the current template apply has been consumed. */
  const undoConsumedRef = useRef(false);

  /** Undo the most recent template application and notify the user. */
  function undoTemplate(): void {
    if (undoConsumedRef.current) return;
    undoConsumedRef.current = true;
    const undone = useSessionHistoryStore.getState().undo();
    if (undone) {
      useNotificationStore.getState().addNotification({
        type: 'info',
        title: 'Template application undone',
        message: 'The template changes have been reverted.',
      });
    } else {
      useNotificationStore.getState().addNotification({
        type: 'warning',
        title: 'Undo failed',
        message: 'Could not revert the template changes.',
      });
    }
  }

  // ---------------------------------------------------------------------------
  // applyTemplate
  // ---------------------------------------------------------------------------

  function applyTemplate(
    template: TemplateDefinition,
    options?: TemplateApplyOptions
  ): TemplateApplyResult {
    {
      // Reset undo guard for this new apply
      undoConsumedRef.current = false;

      // 1. Validate the template
      const validation = validateTemplate(template);
      if (!validation.success) {
        return { success: false, error: validation.errors.join('; ') };
      }

      // 1b. Rewrite #workflowname# placeholders (upstream templates use these
      //     to namespace connection/parameter names per workflow). Prefer the
      //     workflow identity from the store; fall back to the URL params so
      //     a slow MSAL handshake doesn't strand the rewrite with an empty
      //     name. Always run the rewrite so the placeholder never leaks into
      //     the workflow definition.
      const workflowName =
        useWorkflowStore.getState().currentWorkflowIdentity?.workflow ||
        urlWorkflowName ||
        '';
      const resolvedTemplate = preferManagedTemplateAgents(
        stripWorkflowParameters(rewriteWorkflowNamePlaceholders(template, workflowName)),
        defaultModelId,
      );

      // 2. Resolve connections against the user's existing references
      //    (done before snapshot so early-return doesn't pollute undo history)
      const connectionResult = resolveTemplateConnections(
        resolvedTemplate.connectionRequirements,
        useConnectionStore.getState().references,
        findCompatibleConnectionsByApiId,
      );

      // 2b. Build placeholder connection configuration for unresolved connections.
      //     This ensures the workflow loads with proper API IDs so the designer can
      //     show connector icons and prompt the user to configure connections.
      const placeholderConnectionsConfig = buildPlaceholderConnectionsConfig(
        connectionResult.unresolved
      );

      // 3. Build connectionMap: templateReferenceKey → resolved referenceKey.
      //    A template applies immediately even when connections need selection —
      //    unresolved ones get placeholder entries and the user fixes them later
      //    via the Connections dialog. We still honour any selections the caller
      //    passes (e.g. the gallery picker) and auto-matched connections.
      //    Use requirement.referenceKey (the key the template workflow uses in
      //    its action inputs, e.g. "sql_myWorkflow") so patchWorkflowConnections
      //    can find an exact match. Fall back to connectorName for templates
      //    that don't declare an explicit referenceKey.
      const connectionMap = buildConnectionMap(connectionResult, options?.connectionSelections);

      // Map unresolved connections to their reference keys (from the requirement)
      // so the workflow actions keep pointing to the same reference names that
      // will have placeholder entries in connections.json.
      // Note: For unresolved connections, we DON'T need to remap - the workflow
      // already has the correct reference keys from the template definition.
      // The placeholder entries we create use requirement.referenceKey which
      // matches what's already in the workflow.

      // 5. Patch the workflow with the resolved connection reference keys
      let patchedDefinition;
      try {
        patchedDefinition = patchWorkflowConnections(resolvedTemplate.workflowDefinition, connectionMap);
      } catch (err) {
        return { success: false, error: `Failed to patch connections: ${err instanceof Error ? err.message : String(err)}` };
      }

      // 6. Capture pre-apply snapshot for undo (after all precondition checks
      //    so we don't pollute undo history on early returns).
      captureCurrentSnapshot(`pre-template-apply: ${resolvedTemplate.name}`);

      // 7. Apply to canvas — wrap mutations so we can revert on failure
      try {
        // connections.json is shared across all workflows in the app, so we must
        // merge template connections with existing ones (not replace). This
        // preserves connections used by other workflows while adding any new
        // connections defined by the template, plus placeholders for unresolved
        // connections so the designer can show proper connector info.
        const templateConnectionsConfig = resolvedTemplate.connectionsConfiguration as ConnectionsConfiguration | undefined;
        const existingConnectionsConfig = useWorkflowStore.getState().connections;
        const connectionsConfig = mergeConnectionsConfiguration(
          mergeConnectionsConfiguration(existingConnectionsConfig, placeholderConnectionsConfig),
          templateConnectionsConfig
        );

        useWorkflowStore.getState().setWorkflowFiles(
          patchedDefinition,
          connectionsConfig
        );

        // Mark dirty so autosave persists the template as a draft.
        // setWorkflowFiles resets isDirty (designed for loading saved state),
        // but after template apply we need the draft written to disk.
        useWorkflowStore.getState().markDirty();

        // 8. Re-initialise the connection store from the merged connections.
        //    This ensures reference tracking stays consistent and existing
        //    connections (used by other workflows) are preserved alongside
        //    any new connections from the template.
        const connectionStore = useConnectionStore.getState();
        if (typeof connectionStore.initializeFromWorkflow === 'function') {
          connectionStore.initializeFromWorkflow(
            patchedDefinition,
            connectionsConfig
          );
        }
      } catch (err) {
        // Revert to pre-apply snapshot on failure
        useSessionHistoryStore.getState().undo();
        return { success: false, error: `Failed to apply template: ${err instanceof Error ? err.message : String(err)}` };
      }

      // 9. Capture post-apply snapshot (this is the "current" state after apply)
      captureCurrentSnapshot(`template-apply: ${resolvedTemplate.name}`);

      // 10. Telemetry
      telemetryService.trackEvent(
        'template.applied',
        {
          templateId: resolvedTemplate.id,
          templateName: resolvedTemplate.name,
          templateCategory: resolvedTemplate.category.join(','),
        },
        {
          autoMatchedCount: connectionResult.autoMatched.length,
          needsSelectionCount: connectionResult.needsSelection.length,
          unresolvedCount: connectionResult.unresolved.length,
        }
      );

      // 11. Seed mock data from the template into the workflow store so the
      //     Mock Data tab / mocked badges / mock test runs work immediately
      //     in the regular editor (no template mode). Store reads/writes live
      //     here so `@/utils/templates` stays free of store imports.
      {
        const ws = useWorkflowStore.getState();
        ws.setMockedNodes(buildTemplateMockSeed(resolvedTemplate.id, ws.workflow, ws.mockedNodes));
      }

      // 12. Show undo toast unless caller opted out
      if (!options?.silent) {
        const unresolvedCount = connectionResult.unresolved.length;
        const message = unresolvedCount > 0
          ? `"${resolvedTemplate.name}" has been applied. ${unresolvedCount} connection${unresolvedCount > 1 ? 's need' : ' needs'} to be configured.`
          : `"${resolvedTemplate.name}" has been applied to your workflow`;

        useNotificationStore.getState().addNotification({
          type: unresolvedCount > 0 ? 'warning' : 'success',
          title: unresolvedCount > 0 ? 'Template applied — connections needed' : 'Template applied',
          message,
          durationMs: 10_000, // 10s — longer than default for destructive undo
          actionButton: {
            label: 'Undo',
            onClick: () => undoTemplate(),
          },
        });
      }

      return { success: true, connectionResult };
    }
  }

  // ---------------------------------------------------------------------------
  // previewConnections — pure, no side effects
  // ---------------------------------------------------------------------------

  // Read references on-demand via getState() to avoid re-renders whenever
  // any connection reference changes in the store.
  const previewConnections = useCallback(
    (template: TemplateDefinition): TemplateConnectionResolutionResult => {
      return resolveTemplateConnections(
        template.connectionRequirements,
        useConnectionStore.getState().references,
        findCompatibleConnectionsByApiId,
      );
    },
    []
  );

  return { applyTemplate, previewConnections, isManagedModelLoading };
}
